import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { SeasonPassService } from 'src/app/services/season-pass.service';
import { PanelTitleService } from 'src/app/services/panel-title.service';
import { InfoCardComponent } from '../../../shared/info-card/info-card.component';

/**
 * Cobro de un abono desde el panel (taquilla). Se llega aquí justo después de
 * dar de alta el abono. El cobro es siempre presencial, como en el TPV: al
 * pulsar «Cobrar» el pago se registra en el acto (endpoint TPV-directo), sin
 * pasarela ni redirección a Redsys, y el abono queda activado.
 *
 * No se pregunta efectivo o tarjeta: `payDirect` solo envía producto y
 * cantidad, el método de pago no viaja al TPV, así que preguntarlo no
 * cambiaría nada de lo que se factura. Es el mismo criterio que en la
 * renovación de socios legacy (`legacy-renewal-form`), que confirma y cobra
 * en una sola acción.
 */
@Component({
  selector: 'app-pass-checkout',
  templateUrl: './pass-checkout.component.html',
  styleUrls: ['./pass-checkout.component.scss']
})
export class PassCheckoutComponent implements OnInit, OnDestroy {

  business: any;
  seasonPassId: number | null = null;

  loaded: boolean = false;
  waiting: boolean = false;
  loadErrorMsg: string | null = null;

  seasonPass: any = null;
  products: any[] = [];
  selectedProduct: any = null;
  // Nivel de butaca resuelto por el servidor: es el que fija qué abonos se
  // pueden cobrar y a qué precio.
  levelName: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private seasonPassService: SeasonPassService,
    private panelTitleService: PanelTitleService,
  ) { }

  ngOnInit(): void {
    this.business = JSON.parse(localStorage.getItem('business') || '{}');
    this.seasonPassId = +this.route.snapshot.params['passId'];
    this.loadSeasonPass();
  }

  ngOnDestroy(): void {
    this.panelTitleService.setSubtitle(null);
  }

  private loadSeasonPass() {
    this.seasonPassService.getSeasonPassById(this.business.id, this.seasonPassId!).subscribe({
      next: (res) => {
        this.seasonPass = res.data;
        this.panelTitleService.setSubtitle(this.seasonPass?.user?.name || null);
        this.loadProducts();
      },
      error: () => {
        this.loadErrorMsg = 'No hemos podido cargar el abono. Vuelve a intentarlo en unos minutos.';
        this.loaded = true;
      }
    });
  }

  /**
   * Abonos que se le pueden cobrar: los asignados a su categoría Y al nivel de
   * su butaca. El servidor resuelve el nivel y, si no hay nada, dice por qué.
   *
   * Cobro en taquilla, así que se piden también los productos de canal panel
   * (invitaciones, descuentos): es justo la pantalla para la que existen.
   */
  private loadProducts() {
    this.seasonPassService.getAvailableProducts(this.business.id, this.seasonPassId!, true).subscribe({
      next: (res) => {
        this.levelName = res?.data?.level_name || null;
        this.products = (res?.data?.products || []).filter((p: any) => p.active !== false);

        if (!this.products.length) {
          this.loadErrorMsg = res?.data?.reason === 'no_seat'
            ? 'Este abono no tiene butaca asignada. Asígnasela antes de cobrarlo: el abono que paga depende del nivel de su butaca.'
            : 'No hay ningún abono configurado para la categoría de este abonado en el nivel de su butaca. Créalo y asígnalo en la ficha de la temporada antes de cobrar.';
          this.loaded = true;
          return;
        }

        this.selectProduct(this.products[0]);
        this.loaded = true;
      },
      error: () => {
        this.loadErrorMsg = 'No hemos podido cargar los importes de este abono. Vuelve a intentarlo en unos minutos.';
        this.loaded = true;
      }
    });
  }

  selectProduct(product: any) {
    this.selectedProduct = product;
  }

  get subtotal(): number {
    return this.selectedProduct ? +this.selectedProduct.price : 0;
  }

  /**
   * El total es el precio del abono a secas: el cobro en taquilla nunca lleva
   * gastos de gestión, aunque el producto los tenga definidos. Los cobra solo
   * el canal web (`season-pass-checkout`); aquí el TPV los fuerza a 0
   * (`BillingService::calculateProductLines($applyFee = false)`), así que
   * sumar `product.fee` aquí solo haría que la taquilla le pidiese al abonado
   * más de lo que se factura. Pasa con cualquier producto que se venda también
   * por web, que es donde el fee está configurado.
   */
  get totalPrice(): number {
    return this.subtotal;
  }

  get seatDisplay(): string | null {
    if (!this.seasonPass?.seat) return null;
    const parts: string[] = [];
    // Nombre del nivel resuelto por el servidor; `id_section` es una PK del
    // mapa y no dice nada al operador.
    if (this.levelName) parts.push(this.levelName);
    if (this.seasonPass.row) parts.push(`Fila ${this.seasonPass.row}`);
    if (this.seasonPass.seat) parts.push(`Butaca ${this.seasonPass.seat}`);
    return parts.join(' · ');
  }

  /**
   * Cobra el abono en el acto por TPV-directo (emite ticket y activa el abono).
   * Sin pasos intermedios: el botón es la confirmación.
   */
  pay() {
    if (!this.selectedProduct || !this.seasonPass || this.waiting) return;
    this.waiting = true;

    this.seasonPassService.payDirect(this.business.id, this.seasonPassId!, {
      quantity: 1,
      id_product: this.selectedProduct.id,
    }).subscribe({
      next: () => {
        this.waiting = false;
        this.openInfoCard('Cobro registrado correctamente. El abono queda activado.')
          .afterClosed().subscribe(() => {
            this.router.navigate(['/panel-control/sports-passes/passes/edit', this.seasonPassId]);
          });
      },
      error: (err) => {
        this.waiting = false;
        // Se queda en el checkout: el abono ya existe con su pedido pendiente y
        // el operador puede reintentar el cobro sin volver a buscarlo.
        this.openInfoCard(
          this.extractErrorMessage(err, 'No hemos podido registrar el cobro. Inténtalo de nuevo.'),
          false,
        );
      }
    });
  }

  skipPayment() {
    // El abono ya está creado; se puede cobrar más tarde desde su ficha.
    this.router.navigate(['/panel-control/sports-passes/passes']);
  }

  goBack() {
    this.router.navigate(['/panel-control/sports-passes/passes']);
  }

  private extractErrorMessage(err: any, fallback: string): string {
    if (err?.status === 422 && err?.error?.errors && typeof err.error.errors === 'object' && !Array.isArray(err.error.errors)) {
      const first: any = Object.values(err.error.errors)[0];
      if (Array.isArray(first) && first.length) {
        return first[0];
      }
    }
    return err?.error?.message || fallback;
  }

  private openInfoCard(text: string, success: boolean = true) {
    return this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: { text, success }
    });
  }
}
